import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { DashboardSkeleton } from "@/components/DashboardSkeleton";

// Eagerly load Auth (first page users see)
import Auth from "./pages/Auth";

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const LateOrders = lazy(() => import("./pages/LateOrders"));
const QCReview = lazy(() => import("./pages/QCReview"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Profile = lazy(() => import("./pages/Profile"));
const WebhookMonitor = lazy(() => import("./pages/WebhookMonitor"));

const AuditTrail = lazy(() => import("./pages/AuditTrail"));
const OperationsDashboard = lazy(() => import("./pages/OperationsDashboard"));
const MyPerformance = lazy(() => import("./pages/MyPerformance"));
const QCAnalytics = lazy(() => import("./pages/QCAnalytics"));
const HelpRequests = lazy(() => import("./pages/HelpRequests"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAInstallPrompt />
        <BrowserRouter>
          <AuthProvider>
            <NotificationProvider>
              <Suspense fallback={<DashboardSkeleton />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route path="/fast-track" element={<Navigate to="/orders" replace />} />
                  <Route path="/late-orders" element={<LateOrders />} />
                  <Route path="/qc-review" element={<QCReview />} />
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/knowledge-base" element={<KnowledgeBase />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/webhook-monitor" element={<WebhookMonitor />} />
                  <Route path="/completed-orders" element={<Navigate to="/orders" replace />} />
                  <Route path="/audit-trail" element={<AuditTrail />} />
                  <Route path="/operations" element={<OperationsDashboard />} />
                  <Route path="/my-performance" element={<MyPerformance />} />
                  <Route path="/qc-analytics" element={<QCAnalytics />} />
                  <Route path="/help-requests" element={<HelpRequests />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </NotificationProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
